import { dnd } from '../Core/DragAndDrop';
import DraggableAwareMixins from './DraggableAwareMixins';

export default {
  mixins: [DraggableAwareMixins],
  props: {
    assistiveText: {
      type: String,
    },
    item: {
      type: Object,
      required: true,
    },
    tag: {
      type: String,
      default: 'div',
    },
    acceptsData: {
      type: Function,
      default: () => true,
    },
    allowMultipleDrag: {
      type: Boolean,
      default: false,
    },
    replaceDrag: {
      type: Boolean,
      default: false,
    },
    disableTabindex: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      droppableId: null,
      drop: {},
      forceRecomputeCounter: 0,
    };
  },
  computed: {
    dropIn() {
      if (this.draggableInProgress) {
        return this.draggableTop === this && !!this.draggableSource;
      } else {
        return false;
      }
    },
    droppableAllowed() {
      this.forceRecomputeCounter;

      if (this.draggableInProgress) {
        return this.draggableSource.isDragging
          ? this.isDroppable() && this.effectiveAcceptsData(this.draggableData)
          : this.draggableSource.isSelected &&
              this.effectiveAcceptsData(this.draggableData);
      } else {
        return false;
      }
    },
    cssClasses() {
      let clazz = {
        drop: true,
      };

      return {
        ...clazz,
        'drop-dropzone-occupied': this.drop.dropped,
        'drop-progress':
          !this.dropIn && this.draggableInProgress && !this.drop.dropped,
        'drop-zone': this.dropIn && !this.drop.dropped,
      };
    },
    attributeBindings() {
      let attributes = {
        ...this.$attrs,
        role: 'link',
      };

      if (!this.dropIn && this.draggableInProgress && !this.drop.dropped) {
        attributes['tabindex'] = this.disableTabindex ? -1 : 0;
        attributes['aria-dropeffect'] = 'move';
      }

      return attributes;
    },
  },
  mounted() {
    this.droppableId = this._uid;

    dnd.on('draggableMove', this.handleDraggableMove);
    dnd.on('droppableDrop', this.handleDroppableDrop);

    this.$el.addEventListener('keydown', this.handleKeydown);
    this.$el.addEventListener('click', this.handleOpen);
  },
  methods: {
    handleDraggableMove() {
      if (!this.drop.dropped || this.allowMultipleDrag) {
        if (this.isDroppable()) {
          dnd.doDroppableOver(this);
          this.$emit('onDropOver');
        } else {
          if (this === this.draggableTop) {
            dnd.doDroppableLeave();
          }
        }
      }
    },
    handleDroppableDrop() {
      if (!this.drop.dropped || this.allowMultipleDrag) {
        this.doDroppableDrop();
      }
    },
    handleKeydown(e) {
      if (e.keyCode === 32) {
        e.preventDefault();

        if (!this.drop.dropped || this.allowMultipleDrag) {
          dnd.doDroppableOver(this);
          this.draggableSource.handleUp(e);
        }
      }
    },
    handleOpen(e) {
      e.preventDefault();
      if (this.drop.dropped) {
        this.$emit('onDropOpen', { ...this.drop });
      }
    },
    doDroppableDrop() {
      this.forceRecomputeCounter++;
      if (this.draggableTop === this) {
        const isDroppable = this.droppableAllowed;

        Object.assign(this.draggableSource.drag, {
          attempts: this.draggableSource.drag.attempts + 1,
        });

        if (isDroppable) {
          Object.assign(this.draggableSource.drag, { dropped: true });

          if (this.allowMultipleDrag) {
            let drags = this.drop.drag || [];
            drags.push(this.draggableSource.drag);

            const dropped = this.drop.dragExpected.length === drags.length;

            this.drop = Object.assign({}, this.drop, {
              dropped: dropped,
              drag: drags,
            });
          } else {
            this.drop = Object.assign({}, this.drop, {
              dropped: true,
              drag: this.draggableSource.drag,
            });
          }

          this.$emit('onDropCorrect', { ...this.drop });
        } else {
          this.$emit('onDropIncorrect', { ...this.drop });
        }
      }
    },
    effectiveAcceptsData(draggableData) {
      return this.acceptsData(draggableData);
    },
    getOffset(element) {
      const offset = element.getBoundingClientRect();
      return {
        top: offset.top / this.draggableSource.scale,
        left: offset.left / this.draggableSource.scale,
      };
    },
    isDroppable() {
      const offsetDrag = this.getOffset(this.draggableElement);
      const widthDrag = this.draggableElement.offsetWidth;
      const heightDrag = this.draggableElement.offsetHeight;

      const offsetDrop = this.getOffset(this.$el);
      const widthDrop = this.$el.offsetWidth;
      const heightDrop = this.$el.offsetHeight;

      return !(
        offsetDrop.left > offsetDrag.left + widthDrag - widthDrag / 2 ||
        offsetDrop.left + widthDrop < offsetDrag.left + widthDrag / 2 ||
        offsetDrop.top > offsetDrag.top + heightDrag - heightDrag / 2 ||
        offsetDrop.top + heightDrop < offsetDrag.top + heightDrag / 2
      );
    },
  },
  watch: {
    item: {
      immediate: true,
      handler: function (drop) {
        this.drop = drop;
      },
    },
  },
  destroyed() {
    // Both lines has been commented as an immediate solution to problem in W.T.M Properties where
    // the drop event does not working when press back button
    // TODO: check for better solution for good practices
    // dnd.off('draggableMove')
    // dnd.off('droppableDrop')
  },
};
